import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Directive,
  ElementRef,
  inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DxDropDownBoxComponent, DxTreeViewComponent } from 'devextreme-angular';
import { ValueChangedEvent } from 'devextreme/ui/drop_down_box';
import { ItemSelectionChangedEvent } from 'devextreme/ui/tree_view';
import { debounceTime, first, map, Observable, Subject } from 'rxjs';
import { CategoryService } from 'src/app/components/admin/category/category.service';
import { SelectorOption } from 'src/app/interfaces/form-and-field.interface';
import { Categories } from 'src/app/swagger-api';
import { MultipleSearchMapFormFieldService } from '../multiple-search-map-form-field/helpers/multiple-search-map-form-field.service';
import { SuggestionSearchFieldShared } from '../suggestion-search-field.shared';

// Private interface for Tree View
interface DxTreeItem {
  id: string;
  text: string;
  items?: DxTreeItem[];
}

@Directive()
abstract class MultipleSearchFormTreeComponent extends SuggestionSearchFieldShared implements OnInit {
  @ViewChild(DxTreeViewComponent, { static: false }) treeView: DxTreeViewComponent;
  @ViewChild(DxDropDownBoxComponent, { read: ElementRef, static: false }) dropBox: ElementRef<HTMLElement>;

  private DEFAULT_MAX_AMOUNT = 20;

  private changeDetector = inject(ChangeDetectorRef);

  private searchTerms = new Subject<string>();

  protected searchFormFieldValueService = inject(MultipleSearchMapFormFieldService);

  // Tree view items
  public treeData: DxTreeItem[] = [];
  // Subset of tree view items
  public filteredTreeData: DxTreeItem[] = [];

  // The dropdown has a search bar where the user can filter the tree view items
  public searchValue = '';

  public selectedItems: SelectorOption[] = [];
  public selectedItemText: string[] = [];

  // Extend this method to populate the tree data initially (i.e. specialties or diseases)
  protected abstract getInitialTreeData(): Observable<DxTreeItem[]>;

  override ngOnInit() {
    super.ngOnInit();

    if (this.fieldAttribute.hidden) {
      this.control.disable();
    } else {
      this.control.enable();
    }

    this.subscriptions.push(
      this.form.statusChanges.subscribe(() => {
        this.changeDetector.markForCheck();
      }),
    );

    this.subscriptions.push(
      this.searchFormFieldValueService.attributeChanged.subscribe(() => {
        this.changeDetector.markForCheck();
      }),
    );

    // If an external component resets the form (or component) we should re-initialize the selected items
    this.subscriptions.push(
      this.control.valueChanges.subscribe((value?: SelectorOption[]) => {
        if (value === undefined || value === null || (value && value.length == 0)) {
          this.searchValue = '';
          this.selectedItems = [];
          this.selectedItemText = [];
          this.filteredTreeData = this.treeData.slice(0, this.DEFAULT_MAX_AMOUNT);
          this.treeView?.instance.unselectAll();
          this.treeView?.instance.collapseAll();

          this.changeDetector.markForCheck();
        }
      }),
    );

    // Initialize the dropdown
    this.getInitialTreeData()
      .pipe(first())
      .subscribe((treeData) => {
        this.treeData = treeData;
        this.filteredTreeData = treeData.slice(0, this.DEFAULT_MAX_AMOUNT);
      });

    // Subscribe to search terms with debounce
    this.searchTerms.pipe(debounceTime(200)).subscribe((searchTerm) => {
      const filteredTreeData = this.treeData.filter(
        (item) =>
          item.text.toLowerCase().includes(searchTerm) ||
          item.items?.some((subItem) => subItem.text.toLowerCase().includes(searchTerm)),
      );

      const normalizedSearch: string = searchTerm.trim().toLowerCase();
      this.filteredTreeData = filteredTreeData
        .sort((a: DxTreeItem, b: DxTreeItem) => {
          const leftRaw = a.text.trim();
          const rightRaw = b.text.trim();

          const left = leftRaw.toLowerCase();
          const right = rightRaw.toLowerCase();

          // If we have a search term, prioritize "startsWith"
          if (normalizedSearch) {
            const leftStarts = left.startsWith(normalizedSearch);
            const rightStarts = right.startsWith(normalizedSearch);

            if (leftStarts && !rightStarts) return -1;
            if (!leftStarts && rightStarts) return 1;
          }

          // Fallback to your original locale-aware sort
          return leftRaw.localeCompare(rightRaw, undefined, {
            sensitivity: 'base',
            numeric: true,
          });
        })
        .slice(0, this.DEFAULT_MAX_AMOUNT);
      this.changeDetector.markForCheck();
    });
  }

  public onOpened(): void {
    this.updateClassList();
    this.updateSelection();
  }

  public isDisabled(): boolean {
    return this.fieldAttribute.disabled;
  }

  public updateSelection(): void {
    if (!this.treeView?.instance) {
      return;
    }

    this.selectedItems.forEach((item: SelectorOption) => {
      this.treeView?.instance.selectItem(item.entity_id);
    });
  }

  public async onItemSelectionChanged(event: ItemSelectionChangedEvent): Promise<void> {
    const selectedItemIds = new Set(this.selectedItems.map((i) => i.entity_id));

    // Idempotency checks
    if (event.itemData.selected && selectedItemIds.has(String(event.itemData.id))) return;
    if (!event.itemData.selected && !selectedItemIds.has(String(event.itemData.id))) return;

    if (event.itemData.selected) {
      this.selectedItems.push({ entity_id: String(event.itemData.id), title: event.itemData.text });
    } else {
      this.selectedItems = this.selectedItems.filter((i) => i.entity_id !== String(event.itemData.id));
    }

    this.selectedItemText = [...this.selectedItems.map((i) => i.title)];

    // Update the form control
    this.control.setValue([...this.selectedItems]);

    this.searchFormFieldValueService.setFieldValue({
      field: this.fieldAttribute.backendAttribute.name,
      selectedItems: this.selectedItems,
    });

    // Expand the tree view item (if parent)
    await this.treeView?.instance.expandItem(event.itemData);
  }

  public onDropDownBoxValueChanged(event: ValueChangedEvent): void {
    if (!event.value) {
      this.treeView?.instance.unselectAll();
      this.treeView?.instance.collapseAll();
      this.selectedItems = [];
      this.control.setValue([]);
      this.searchFormFieldValueService.setFieldValue({
        field: this.fieldAttribute.backendAttribute.name,
        selectedItems: [],
      });
    }
  }

  protected initSelectedItems(): void {
    const controlValue: SelectorOption[] = this.control.value ? (this.control.value as SelectorOption[]) : [];

    if (controlValue.length < 1) return;

    this.selectedItems = controlValue;
    this.selectedItemText = controlValue.map((v) => v.title);

    this.control.setValue([...this.selectedItems]);
    this.searchFormFieldValueService.setFieldValue({
      field: this.fieldAttribute.backendAttribute.name,
      selectedItems: this.selectedItems,
    });
  }

  private updateClassList(): void {
    const dxPopup: HTMLElement | undefined = document.getElementsByClassName('dx-popup-content').item(0) as HTMLElement;
    if (dxPopup) {
      dxPopup.classList.add('drop-down');
    }
  }

  protected getSelectedItems(): SelectorOption[] {
    const selectedNodes = this.treeView.instance.getSelectedNodes() || [];

    return selectedNodes.map((node) => ({
      entity_id: String(node.itemData.id),
      title: String(node.itemData.text),
    }));
  }

  public onBlur(): void {
    this.control.markAsTouched();
  }

  public checkErrors(): void {
    const selectedItemIds = this.selectedItems.map((i) => i.entity_id);

    this.formValidationService.checkMultipleSearchFieldValidationErrors(
      this.control,
      selectedItemIds.length > 0 ? selectedItemIds.join(', ') : '',
    );
  }

  public onSearchValueChanged(e): void {
    type InputEventType = { event?: { target?: { value?: string } } };
    const _e = e as InputEventType;

    const searchTerm: string = _e.event?.target?.value?.toLowerCase();

    this.searchValue = searchTerm;

    if (!searchTerm) {
      this.filteredTreeData = this.treeData.slice(0, this.DEFAULT_MAX_AMOUNT);
      return;
    }

    this.searchTerms.next(searchTerm);
  }

  protected onControlDisabled(): void {
    if (this.multipleSearchInput) {
      this.multipleSearchInput.nativeElement.value = '';
    }
  }
}

const parseCategories = (categories: Categories): DxTreeItem[] => {
  return categories.items.map((c) => ({
    id: c.entity_id,
    text: c.title,
    items: c.subcategories?.map((s) => ({
      id: s.entity_id,
      text: s.title,
    })),
  }));
};

@Component({
  selector: 'app-diseases-select-tree',
  templateUrl: './multiple-search-form-tree.component.html',
  styleUrls: ['./multiple-search-form-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DiseasesSelectTreeComponent extends MultipleSearchFormTreeComponent {
  private categoryService = inject(CategoryService);

  protected getInitialTreeData(): Observable<DxTreeItem[]> {
    return this.categoryService.getDiseases().pipe(first(), map(parseCategories));
  }
}

@Component({
  selector: 'app-specialties-select-tree',
  templateUrl: './multiple-search-form-tree.component.html',
  styleUrls: ['./multiple-search-form-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpecialtiesSelectTreeComponent extends MultipleSearchFormTreeComponent {
  private categoryService = inject(CategoryService);

  protected getInitialTreeData(): Observable<DxTreeItem[]> {
    return this.categoryService.getSpecialties().pipe(first(), map(parseCategories));
  }
}

@Component({
  selector: 'app-charity-focus-tree',
  templateUrl: './multiple-search-form-tree.component.html',
  styleUrls: ['./multiple-search-form-tree.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CharityFocusesSelectTreeComponent extends MultipleSearchFormTreeComponent {
  private categoryService = inject(CategoryService);

  protected getInitialTreeData(): Observable<DxTreeItem[]> {
    return this.categoryService.getCharityFocuses().pipe(first(), map(parseCategories));
  }
}
